import { useEffect, useMemo, useState } from 'react';
import {
  Tabs,
  Card,
  Table,
  Button,
  Tag,
  Space,
  Modal,
  Form,
  Input,
  InputNumber,
  Switch,
  DatePicker,
  Select,
  Tooltip,
  Empty,
  Divider,
  Alert
} from 'antd';
import {
  ReloadOutlined,
  PlusOutlined,
  EditOutlined,
  PauseCircleOutlined,
  StarFilled,
  StarOutlined,
  PercentageOutlined,
  CalculatorOutlined,
  TagOutlined,
  CalendarOutlined,
  TeamOutlined
} from '@ant-design/icons';
import dayjs from 'dayjs';
import { internalGstRates, internalStandardPricing } from '../../api/internalAdminEndpoints';
import { useAppNotifications } from '../../utils/notify';
import LoadingBlock from '../../components/LoadingBlock';
import PageHeader from '../../components/PageHeader';
import useInternalAdminAuth from '../../contexts/InternalAdminAuthContext';
import { paiseToINR } from '../../components/internalAdmin/AccountCells';

// Stage 5N-4: Pricing & Tax master.
//
// Three tabs:
//   1. GST Rates       — list / add / edit / set-default / deactivate
//   2. Standard Plan   — current pricing + edit form
//   3. Live Preview    — quote calculator that hits the live backend
//                         so the operator sees what a customer would
//                         see with the current config

function rupeesToPaise(rupees) {
  if (rupees === null || rupees === undefined || rupees === '') return null;
  const n = Number(rupees);
  if (!Number.isFinite(n)) return null;
  return Math.round(n * 100);
}
function paiseToRupees(paise) {
  if (paise === null || paise === undefined) return null;
  const n = Number(paise);
  if (!Number.isFinite(n)) return null;
  return Math.round(n) / 100;
}

// ============== GST Rates tab =======================================

function GstRatesTab() {
  const { hasPermission } = useInternalAdminAuth();
  const { notifySuccess, notifyError } = useAppNotifications();
  const [loading, setLoading] = useState(true);
  const [rates, setRates] = useState([]);
  const [editing, setEditing] = useState(null); // null | { id?, ...fields }
  const [busy, setBusy] = useState(false);
  const [form] = Form.useForm();

  const canManage = hasPermission('gst_rates.manage');

  async function load() {
    setLoading(true);
    try {
      const r = await internalGstRates.list();
      setRates(Array.isArray(r.items) ? r.items : []);
    } catch (err) {
      notifyError(err, { title: 'Could not load GST rates' });
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => { load(); }, []);

  function openCreate() {
    form.resetFields();
    setEditing({ mode: 'create' });
    form.setFieldsValue({
      rateName: 'GST 18%',
      ratePercent: 18,
      isDefault: false,
      effectiveFrom: dayjs(),
      effectiveUntil: null
    });
  }
  function openEdit(row) {
    form.resetFields();
    setEditing({ mode: 'edit', id: row.id });
    form.setFieldsValue({
      rateName: row.rateName,
      ratePercent: row.ratePercent,
      effectiveFrom: row.effectiveFrom ? dayjs(row.effectiveFrom) : null,
      effectiveUntil: row.effectiveUntil ? dayjs(row.effectiveUntil) : null
    });
  }

  async function onSave(values) {
    const payload = {
      rateName: values.rateName,
      ratePercent: Number(values.ratePercent),
      effectiveFrom: values.effectiveFrom ? values.effectiveFrom.toISOString() : null,
      effectiveUntil: values.effectiveUntil ? values.effectiveUntil.toISOString() : null
    };
    if (editing.mode === 'create') {
      payload.isDefault = values.isDefault === true;
    }
    setBusy(true);
    try {
      if (editing.mode === 'create') {
        await internalGstRates.create(payload);
        notifySuccess('GST rate created');
      } else {
        await internalGstRates.update(editing.id, payload);
        notifySuccess('GST rate updated');
      }
      setEditing(null);
      load();
    } catch (err) {
      notifyError(err, { title: 'Could not save GST rate' });
    } finally {
      setBusy(false);
    }
  }

  async function onSetDefault(row) {
    setBusy(true);
    try {
      await internalGstRates.setDefault(row.id);
      notifySuccess(`${row.rateName} is now the default`);
      load();
    } catch (err) {
      notifyError(err, { title: 'Could not set default' });
    } finally {
      setBusy(false);
    }
  }

  async function onDeactivate(row) {
    Modal.confirm({
      title: 'Deactivate GST rate?',
      content: `${row.rateName} (${row.ratePercent}%) will no longer be selectable for new pricing rows.`,
      okText: 'Deactivate',
      okButtonProps: { danger: true },
      onOk: async () => {
        setBusy(true);
        try {
          await internalGstRates.deactivate(row.id);
          notifySuccess('GST rate deactivated');
          load();
        } catch (err) {
          notifyError(err, { title: 'Could not deactivate GST rate' });
        } finally {
          setBusy(false);
        }
      }
    });
  }

  const columns = useMemo(() => [
    { title: 'Name', dataIndex: 'rateName', key: 'name', width: 180,
      render: (v, r) => (
        <span className="flex items-center gap-2">
          {r.isDefault ? <StarFilled className="text-amber-500" /> : null}
          <span className="font-medium text-ink-900">{v}</span>
        </span>
      ) },
    { title: 'Rate', dataIndex: 'ratePercent', key: 'rate', width: 100, align: 'right',
      render: (v) => <span className="font-mono">{Number(v).toFixed(2)}%</span> },
    { title: 'Effective From', dataIndex: 'effectiveFrom', key: 'from', width: 160,
      render: (v) => v ? new Date(v).toLocaleDateString() : '—' },
    { title: 'Effective Until', dataIndex: 'effectiveUntil', key: 'until', width: 160,
      render: (v) => v ? new Date(v).toLocaleDateString() : 'Open-ended' },
    { title: 'Status', key: 'status', width: 140,
      render: (_v, r) => (
        <Space size={4} wrap={false}>
          {r.isActive ? <Tag color="green">Active</Tag> : <Tag color="default">Inactive</Tag>}
          {r.isDefault ? <Tag color="gold">Default</Tag> : null}
        </Space>
      ) },
    { title: 'Actions', key: 'actions', width: 280, fixed: 'right',
      render: (_v, r) => (
        <Space size={4} wrap={false}>
          <Tooltip title={canManage ? '' : 'You do not have gst_rates.manage'}>
            <Button size="small" icon={<EditOutlined />} disabled={!canManage}
              onClick={() => openEdit(r)}>Edit</Button>
          </Tooltip>
          {!r.isDefault && r.isActive ? (
            <Tooltip title={canManage ? '' : 'You do not have gst_rates.manage'}>
              <Button size="small" icon={<StarOutlined />} disabled={!canManage}
                onClick={() => onSetDefault(r)}>Set default</Button>
            </Tooltip>
          ) : null}
          {!r.isDefault && r.isActive ? (
            <Tooltip title={canManage ? '' : 'You do not have gst_rates.manage'}>
              <Button size="small" danger icon={<PauseCircleOutlined />} disabled={!canManage}
                onClick={() => onDeactivate(r)}>Deactivate</Button>
            </Tooltip>
          ) : null}
        </Space>
      ) }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  ], [canManage]);

  return (
    <div>
      <div className="mb-4 flex justify-between items-center">
        <p className="m-0 text-sm text-ink-500">
          One default GST rate at a time. Rate changes do NOT alter
          already-issued invoices or already-paid subscriptions.
        </p>
        <Space>
          <Button icon={<ReloadOutlined />} onClick={load} disabled={loading}>Refresh</Button>
          <Tooltip title={canManage ? '' : 'You do not have gst_rates.manage'}>
            <Button type="primary" icon={<PlusOutlined />} disabled={!canManage}
              onClick={openCreate}>Add GST rate</Button>
          </Tooltip>
        </Space>
      </div>

      {loading ? (
        <LoadingBlock tip="Loading GST rates…" />
      ) : (
        <div className="bg-white rounded-2xl border border-surface-200 overflow-hidden">
          <Table
            rowKey="id"
            dataSource={rates}
            columns={columns}
            size="middle"
            pagination={false}
            scroll={{ x: 1100 }}
            tableLayout="fixed"
            locale={{
              emptyText: (
                <div className="py-10">
                  <Empty description="No GST rates configured" image={Empty.PRESENTED_IMAGE_SIMPLE} />
                </div>
              )
            }}
          />
        </div>
      )}

      <Modal
        open={Boolean(editing)}
        title={editing && editing.mode === 'create' ? 'Add GST rate' : 'Edit GST rate'}
        okText="Save"
        okButtonProps={{ loading: busy }}
        cancelButtonProps={{ disabled: busy }}
        onCancel={() => (busy ? null : setEditing(null))}
        onOk={() => form.submit()}
        destroyOnHidden
        maskClosable={!busy}
      >
        <Form form={form} layout="vertical" onFinish={onSave}>
          <Form.Item label="Rate name" name="rateName"
            rules={[{ required: true, max: 100 }]}>
            <Input placeholder="e.g. GST 18%" />
          </Form.Item>
          <Form.Item label="Rate %" name="ratePercent"
            rules={[{ required: true, type: 'number', min: 0, max: 100 }]}>
            <InputNumber min={0} max={100} step={0.5} className="!w-full" />
          </Form.Item>
          <div className="grid gap-3 sm:grid-cols-2">
            <Form.Item label="Effective from" name="effectiveFrom">
              <DatePicker className="!w-full" />
            </Form.Item>
            <Form.Item label="Effective until" name="effectiveUntil">
              <DatePicker className="!w-full" />
            </Form.Item>
          </div>
          {editing && editing.mode === 'create' ? (
            <Form.Item name="isDefault" valuePropName="checked">
              <span className="flex items-center justify-between">
                <span>
                  <strong className="block text-sm">Set as default rate</strong>
                  <span className="text-xs text-ink-500">
                    Replaces the current default. Required if this is your first rate.
                  </span>
                </span>
                <Switch />
              </span>
            </Form.Item>
          ) : null}
        </Form>
      </Modal>
    </div>
  );
}

// ============== Standard Plan tab ===================================

function StandardPlanTab({ rates }) {
  const { hasPermission } = useInternalAdminAuth();
  const { notifySuccess, notifyError } = useAppNotifications();
  const [loading, setLoading] = useState(true);
  const [pricing, setPricing] = useState(null);
  const [busy, setBusy] = useState(false);
  const [form] = Form.useForm();
  const benefitType = Form.useWatch('annualBenefitType', form);

  const canManage = hasPermission('pricing.manage');

  async function load() {
    setLoading(true);
    try {
      const p = await internalStandardPricing.get();
      setPricing(p);
      form.setFieldsValue({
        pricePerUserRupees:    paiseToRupees(p.pricePerUserPaise),
        gstRateId:             p.gstRateId,
        minUsers:              p.minUsers,
        maxUsers:              p.maxUsers,
        annualBenefitType:     p.annualBenefitType || 'none',
        annualDiscountPercent: p.annualDiscountPercent,
        annualDiscountRupees:  paiseToRupees(p.annualDiscountPaise),
        annualFreeMonths:      p.annualFreeMonths,
        notes:                 p.notes || ''
      });
    } catch (err) {
      notifyError(err, { title: 'Could not load Standard Plan pricing' });
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  async function onSave(values) {
    const payload = {
      pricePerUserPaise: rupeesToPaise(values.pricePerUserRupees),
      gstRateId: values.gstRateId || undefined,
      minUsers: Number(values.minUsers),
      maxUsers: Number(values.maxUsers),
      annualBenefitType: values.annualBenefitType,
      notes: values.notes || null
    };
    if (values.annualBenefitType === 'percent_discount') {
      payload.annualDiscountPercent = Number(values.annualDiscountPercent);
    } else if (values.annualBenefitType === 'fixed_discount') {
      payload.annualDiscountPaise = rupeesToPaise(values.annualDiscountRupees);
    } else if (values.annualBenefitType === 'free_months') {
      payload.annualFreeMonths = Number(values.annualFreeMonths);
    }
    setBusy(true);
    try {
      await internalStandardPricing.update(payload);
      notifySuccess('Standard Plan pricing updated');
      load();
    } catch (err) {
      notifyError(err, { title: 'Could not update Standard Plan pricing' });
    } finally {
      setBusy(false);
    }
  }

  if (loading || !pricing) return <LoadingBlock tip="Loading Standard Plan pricing…" />;

  return (
    <div>
      <Alert
        type="info"
        showIcon
        className="mb-4"
        message="Pricing changes affect new signups + future renewals only"
        description="Already-paid subscriptions and issued invoices keep their stored pricing snapshot. Saving creates a new pricing version and deactivates the old one."
      />

      <Form form={form} layout="vertical" onFinish={onSave}
        disabled={!canManage}>
        <div className="grid gap-4 lg:grid-cols-3">
          <Card size="small" className="lg:col-span-2"
            title={<span className="flex items-center gap-2"><TagOutlined className="text-brand-500" />Pricing &amp; users</span>}>
            <div className="grid gap-3 sm:grid-cols-2">
              <Form.Item label="Monthly price per user (₹)" name="pricePerUserRupees"
                rules={[{ required: true, type: 'number', min: 1, max: 1000000 }]}>
                <InputNumber min={1} max={1000000} step={1} precision={2} className="!w-full" prefix="₹" />
              </Form.Item>
              <Form.Item label="GST rate" name="gstRateId" rules={[{ required: true }]}>
                <Select
                  options={(rates || []).filter((r) => r.isActive).map((r) => ({
                    value: r.id,
                    label: `${r.rateName} — ${Number(r.ratePercent).toFixed(2)}%${r.isDefault ? ' (default)' : ''}`
                  }))}
                />
              </Form.Item>
              <Form.Item label="Min users" name="minUsers"
                rules={[{ required: true, type: 'number', min: 1, max: 10000 }]}>
                <InputNumber min={1} max={10000} className="!w-full" />
              </Form.Item>
              <Form.Item label="Max users" name="maxUsers"
                rules={[{ required: true, type: 'number', min: 1, max: 10000 }]}>
                <InputNumber min={1} max={10000} className="!w-full" />
              </Form.Item>
            </div>

            <Divider style={{ margin: '8px 0 16px' }} />

            <Form.Item label="Annual benefit" name="annualBenefitType"
              rules={[{ required: true }]}
              tooltip="Only one annual benefit at a time. Discount applies before GST. Free months extend access without changing taxable.">
              <Select
                options={[
                  { value: 'none',             label: 'No benefit' },
                  { value: 'percent_discount', label: 'Percent discount' },
                  { value: 'fixed_discount',   label: 'Fixed INR discount' },
                  { value: 'free_months',      label: 'Free months' }
                ]}
              />
            </Form.Item>

            {benefitType === 'percent_discount' ? (
              <Form.Item label="Annual discount %" name="annualDiscountPercent"
                rules={[{ required: true, type: 'number', min: 0.01, max: 100 }]}>
                <InputNumber min={0.01} max={100} step={0.5} className="!w-full" addonAfter="%" />
              </Form.Item>
            ) : null}
            {benefitType === 'fixed_discount' ? (
              <Form.Item label="Annual discount (₹)" name="annualDiscountRupees"
                rules={[{ required: true, type: 'number', min: 1 }]}>
                <InputNumber min={1} step={1} precision={2} className="!w-full" prefix="₹" />
              </Form.Item>
            ) : null}
            {benefitType === 'free_months' ? (
              <Form.Item label="Free months on annual plan" name="annualFreeMonths"
                rules={[{ required: true, type: 'number', min: 0, max: 24 }]}>
                <InputNumber min={0} max={24} className="!w-full" />
              </Form.Item>
            ) : null}

            <Form.Item label="Notes" name="notes">
              <Input.TextArea rows={2} maxLength={1000}
                placeholder="Reason for this pricing change (audit log only)" />
            </Form.Item>

            <div className="flex justify-end">
              <Tooltip title={canManage ? '' : 'You do not have pricing.manage'}>
                <Button type="primary" htmlType="submit" loading={busy} disabled={!canManage}
                  icon={<EditOutlined />}>
                  Save pricing
                </Button>
              </Tooltip>
            </div>
          </Card>

          <Card size="small"
            title={<span className="flex items-center gap-2"><CalculatorOutlined className="text-brand-500" />Current snapshot</span>}>
            <p className="m-0 text-xs text-ink-500">
              Active version. Customers see this on the SignupPaymentPage.
            </p>
            <Divider style={{ margin: '8px 0' }} />
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-ink-500">Monthly / user</span>
                <span className="font-bold">{paiseToINR(pricing.pricePerUserPaise)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-ink-500">GST</span>
                <span>{pricing.gstPercent}%</span>
              </div>
              <div className="flex justify-between">
                <span className="text-ink-500">Min users</span>
                <span>{pricing.minUsers}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-ink-500">Max users</span>
                <span>{pricing.maxUsers}</span>
              </div>
              <Divider style={{ margin: '6px 0' }} />
              <div className="flex justify-between">
                <span className="text-ink-500">Annual benefit</span>
                <span className="font-medium">
                  {pricing.annualBenefitType === 'percent_discount'
                    ? `${pricing.annualDiscountPercent}% off`
                    : pricing.annualBenefitType === 'fixed_discount'
                      ? `${paiseToINR(pricing.annualDiscountPaise)} off`
                      : pricing.annualBenefitType === 'free_months'
                        ? `${pricing.annualFreeMonths} free months`
                        : 'None'}
                </span>
              </div>
            </div>
          </Card>
        </div>
      </Form>
    </div>
  );
}

// ============== Live preview tab ====================================

function PreviewTab() {
  const { notifyError } = useAppNotifications();
  const [userCount, setUserCount] = useState(5);
  const [billingCycle, setBillingCycle] = useState('annual');
  const [loading, setLoading] = useState(false);
  const [quote, setQuote] = useState(null);

  async function run() {
    setLoading(true);
    try {
      const q = await internalStandardPricing.preview({ userCount, billingCycle });
      setQuote(q);
    } catch (err) {
      notifyError(err, { title: 'Preview failed' });
      setQuote(null);
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => { run(); /* eslint-disable-next-line */ }, [userCount, billingCycle]);

  return (
    <div>
      <Card size="small" className="mb-4"
        title={<span className="flex items-center gap-2"><CalculatorOutlined className="text-brand-500" />Quote inputs</span>}>
        <div className="grid gap-3 sm:grid-cols-3">
          <div>
            <label className="text-xs uppercase tracking-wider text-ink-500 block mb-1">User count</label>
            <InputNumber min={1} max={10000} value={userCount} onChange={setUserCount} className="!w-full" />
          </div>
          <div>
            <label className="text-xs uppercase tracking-wider text-ink-500 block mb-1">Billing cycle</label>
            <Select value={billingCycle} onChange={setBillingCycle} className="!w-full"
              options={[{ value: 'monthly', label: 'Monthly' }, { value: 'annual', label: 'Annual' }]} />
          </div>
          <div className="flex items-end">
            <Button icon={<ReloadOutlined />} onClick={run} loading={loading} block>Re-quote</Button>
          </div>
        </div>
      </Card>

      {loading ? (
        <LoadingBlock tip="Quoting…" height={120} />
      ) : !quote ? (
        <Empty description="No quote yet" />
      ) : (
        <Card size="small"
          title={<span className="flex items-center gap-2"><CalendarOutlined className="text-brand-500" />Quote ({quote.userCount} user{quote.userCount === 1 ? '' : 's'} · {quote.billingCycle})</span>}>
          <div className="grid gap-3 lg:grid-cols-2">
            <div className="space-y-2 text-sm">
              <div className="flex justify-between"><span className="text-ink-500">Base</span><span>{paiseToINR(quote.basePaise)}</span></div>
              <div className="flex justify-between"><span className="text-ink-500">Discount</span><span className="text-rose-600">{quote.discountPaise > 0 ? `− ${paiseToINR(quote.discountPaise)}` : '—'}</span></div>
              <div className="flex justify-between"><span className="text-ink-500">Taxable</span><span>{paiseToINR(quote.taxablePaise)}</span></div>
              <div className="flex justify-between"><span className="text-ink-500">GST {quote.gstPercent}%</span><span>{paiseToINR(quote.gstPaise)}</span></div>
              <Divider style={{ margin: '6px 0' }} />
              <div className="flex justify-between text-base font-bold">
                <span>Total payable</span><span>{paiseToINR(quote.totalPaise)}</span>
              </div>
            </div>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between"><span className="text-ink-500">Paid months</span><span>{quote.paidMonths}</span></div>
              <div className="flex justify-between"><span className="text-ink-500">Free months</span><span>{quote.annualFreeMonths || 0}</span></div>
              <div className="flex justify-between"><span className="text-ink-500">Access months</span><span className="font-medium">{quote.accessMonths}</span></div>
              <div className="flex justify-between"><span className="text-ink-500">Annual benefit</span>
                <Tag color="blue" bordered={false}>
                  {quote.annualBenefitType === 'percent_discount' ? `${quote.annualDiscountPercent}% off`
                    : quote.annualBenefitType === 'fixed_discount' ? `${paiseToINR(quote.annualDiscountPaise)} off`
                    : quote.annualBenefitType === 'free_months' ? `${quote.annualFreeMonths} free months`
                    : 'None'}
                </Tag>
              </div>
            </div>
          </div>
        </Card>
      )}
    </div>
  );
}

// ============== Outer page ===========================================

export default function PricingPage() {
  const [rates, setRates] = useState([]);

  // Pre-load rates so the Standard Plan tab can populate the
  // gst_rate_id dropdown without an extra fetch.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await internalGstRates.list();
        if (!cancelled) setRates(Array.isArray(r.items) ? r.items : []);
      } catch (_e) { /* GstRatesTab will retry */ }
    })();
    return () => { cancelled = true; };
  }, []);

  return (
    <div>
      <PageHeader
        title="Pricing &amp; Tax"
        subtitle="Manage GST rates and Standard Plan pricing. Changes apply to new signups and future renewals only."
      />

      <Tabs
        defaultActiveKey="gst"
        size="large"
        items={[
          { key: 'gst',     label: <span className="flex items-center gap-2"><PercentageOutlined />GST Rates</span>,    children: <GstRatesTab /> },
          { key: 'std',     label: <span className="flex items-center gap-2"><TagOutlined />Standard Plan</span>,        children: <StandardPlanTab rates={rates} /> },
          { key: 'preview', label: <span className="flex items-center gap-2"><CalculatorOutlined />Live Preview</span>, children: <PreviewTab /> }
        ]}
      />
    </div>
  );
}
